import React, { useState, useEffect } from 'react';
import { Save, Loader, CheckCircle, Globe, Facebook, Instagram, Phone, Image } from 'lucide-react';
import api from '../../utils/api';
import { useSettings } from '../../context/SettingsContext';

const inputCls = "w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-apex-blue-500";

function Section({ title, icon: Icon, children }) {
  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-5">
      <h2 className="font-black text-apex-blue-700 text-base mb-4 pb-3 border-b border-gray-100 flex items-center gap-2">
        {Icon && <Icon className="w-4 h-4" />}{title}
      </h2>
      {children}
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-xs font-bold text-gray-500 uppercase tracking-wide">{label}</span>
      {children}
      {hint && <span className="text-[10px] text-gray-400">{hint}</span>}
    </label>
  );
}

function ChangePasswordSection() {
  const [form, setForm] = useState({ email:'', current:'', newPw:'', confirm:'' });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg]   = useState('');
  const [err, setErr]   = useState('');

  const save = async () => {
    setMsg(''); setErr('');
    if (!form.email || !form.current || !form.newPw) { setErr('All fields required.'); return; }
    if (form.newPw !== form.confirm) { setErr('Passwords do not match.'); return; }
    if (form.newPw.length < 8) { setErr('Password must be at least 8 characters.'); return; }
    setSaving(true);
    try {
      await api.post('/api/auth/change-password', { email: form.email, current_password: form.current, new_password: form.newPw });
      setMsg('✅ Password changed! Use your new password next time you log in.');
      setForm({ email:'', current:'', newPw:'', confirm:'' });
    } catch(e) { setErr(e.response?.data?.detail || 'Failed to change password.'); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <label className="flex flex-col gap-1.5">
          <span className="text-xs font-bold text-gray-500 uppercase">Admin Email</span>
          <input value={form.email} onChange={e => setForm(f=>({...f,email:e.target.value}))} className={inputCls} placeholder="admin@apextowing.com.au" />
        </label>
        <label className="flex flex-col gap-1.5">
          <span className="text-xs font-bold text-gray-500 uppercase">Current Password</span>
          <input type="password" value={form.current} onChange={e => setForm(f=>({...f,current:e.target.value}))} className={inputCls} placeholder="Your current password" />
        </label>
        <label className="flex flex-col gap-1.5">
          <span className="text-xs font-bold text-gray-500 uppercase">New Password</span>
          <input type="password" value={form.newPw} onChange={e => setForm(f=>({...f,newPw:e.target.value}))} className={inputCls} placeholder="Min. 8 characters" />
        </label>
        <label className="flex flex-col gap-1.5">
          <span className="text-xs font-bold text-gray-500 uppercase">Confirm New Password</span>
          <input type="password" value={form.confirm} onChange={e => setForm(f=>({...f,confirm:e.target.value}))} className={inputCls} placeholder="Repeat new password" />
        </label>
      </div>
      {err && <div className="bg-red-50 border border-red-200 text-red-700 text-xs px-4 py-3 rounded-xl">{err}</div>}
      {msg && <div className="bg-green-50 border border-green-200 text-green-700 text-xs px-4 py-3 rounded-xl">{msg}</div>}
      <button onClick={save} disabled={saving} className="flex items-center gap-2 bg-apex-blue-700 text-white px-5 py-2.5 rounded-xl font-bold text-sm hover:bg-apex-blue-600 disabled:opacity-50">
        {saving ? <Loader className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Change Password
      </button>
    </div>
  );
}

export default function Settings() {
  const { refresh } = useSettings();
  const [general, setGeneral] = useState({});
  const [pricing, setPricing] = useState({});
  const [loading, setLoading] = useState(true);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const [g, p] = await Promise.all([
          api.get('/api/settings/general'),
          api.get('/api/settings/pricing'),
        ]);
        setGeneral(g.data || {});
        setPricing(p.data || {});
      } catch (e) {
        console.error('Settings load error:', e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const sg = k => e => setGeneral(s => ({ ...s, [k]: e.target.value }));
  const sp = k => e => setPricing(s => ({ ...s, [k]: e.target.value }));

  const save = async () => {
    try {
      await Promise.all([
        api.put('/api/settings/general', general),
        api.put('/api/settings/pricing', pricing),
      ]);
      await refresh(); // Push new settings to all pages immediately
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (e) {
      alert('Failed to save settings. Check backend is running.');
    }
  };

  if (loading) return (
    <div className="flex justify-center py-16">
      <Loader className="w-8 h-8 text-apex-blue-500 animate-spin" />
    </div>
  );

  return (
    <div className="p-4 md:p-6 max-w-4xl mx-auto">
      {saved && (
        <div className="fixed bottom-6 right-6 bg-green-600 text-white px-5 py-3 rounded-xl font-semibold text-sm shadow-xl z-50 flex items-center gap-2">
          <CheckCircle className="w-4 h-4" /> Settings saved — all pages updated!
        </div>
      )}

      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-2xl font-black text-apex-blue-700">Settings</h1>
          <p className="text-sm text-gray-400 mt-0.5">Changes apply to every page and invoice immediately</p>
        </div>
        <button onClick={save} className="flex items-center gap-2 bg-apex-blue-700 text-white px-5 py-2.5 rounded-xl font-bold hover:bg-apex-blue-600">
          <Save className="w-4 h-4" /> Save All
        </button>
      </div>

      <div className="space-y-4">

        {/* Logo & Branding */}
        <Section title="Logo & Branding" icon={Image}>
          <div className="grid grid-cols-1 gap-4">
            <Field label="Logo URL" hint="Paste a direct image link (e.g. from Google Drive, Imgur, or your website). Leave blank to use the default truck icon.">
              <input value={general.logo_url || ''} onChange={sg('logo_url')} className={inputCls} placeholder="https://example.com/your-logo.png" />
            </Field>
            {general.logo_url && (
              <div className="bg-gray-50 rounded-xl p-4 flex items-center gap-4">
                <img src={general.logo_url} alt="Logo preview" className="h-16 object-contain" onError={e => e.target.style.display='none'} />
                <div>
                  <p className="text-xs font-bold text-gray-500">LOGO PREVIEW</p>
                  <p className="text-xs text-gray-400 mt-0.5">This appears in the navbar and on invoices</p>
                </div>
              </div>
            )}
          </div>
        </Section>

        {/* Company Information */}
        <Section title="Company Information">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Company Name">
              <input value={general.company_name || ''} onChange={sg('company_name')} className={inputCls} />
            </Field>
            <Field label="ABN">
              <input value={general.company_abn || ''} onChange={sg('company_abn')} className={inputCls} placeholder="XX XXX XXX XXX" />
            </Field>
            <Field label="Phone Number" hint="This number appears on all pages, invoices, and click-to-call buttons">
              <input value={general.company_phone || ''} onChange={sg('company_phone')} className={inputCls} placeholder="07 0000 0000" />
            </Field>
            <Field label="Email Address">
              <input value={general.company_email || ''} onChange={sg('company_email')} className={inputCls} placeholder="info@apextowing.com.au" />
            </Field>
            <Field label="Website">
              <input value={general.company_website || ''} onChange={sg('company_website')} className={inputCls} placeholder="www.apextowing.com.au" />
            </Field>
            <Field label="Address / Service Area">
              <input value={general.company_address || ''} onChange={sg('company_address')} className={inputCls} placeholder="Brisbane, QLD" />
            </Field>
          </div>
        </Section>

        {/* Social Media */}
        <Section title="Social Media & Contact Links" icon={Globe}>
          <div className="bg-blue-50 rounded-xl px-4 py-3 mb-4 text-xs text-apex-blue-700 font-medium">
            These links appear in the website footer and contact section. Leave blank to hide.
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="WhatsApp Number" hint="Include country code, e.g. 61400000000 (no spaces)">
              <div className="flex items-center gap-2">
                <span className="text-green-600 shrink-0">💬</span>
                <input value={general.whatsapp || ''} onChange={sg('whatsapp')} className={inputCls} placeholder="61400000000" />
              </div>
            </Field>
            <Field label="Facebook Page URL">
              <div className="flex items-center gap-2">
                <Facebook className="w-4 h-4 text-blue-600 shrink-0" />
                <input value={general.facebook_url || ''} onChange={sg('facebook_url')} className={inputCls} placeholder="https://facebook.com/yourpage" />
              </div>
            </Field>
            <Field label="TikTok Profile URL">
              <div className="flex items-center gap-2">
                <span className="shrink-0 text-sm">🎵</span>
                <input value={general.tiktok_url || ''} onChange={sg('tiktok_url')} className={inputCls} placeholder="https://tiktok.com/@yourhandle" />
              </div>
            </Field>
            <Field label="Instagram Profile URL">
              <div className="flex items-center gap-2">
                <Instagram className="w-4 h-4 text-pink-500 shrink-0" />
                <input value={general.instagram_url || ''} onChange={sg('instagram_url')} className={inputCls} placeholder="https://instagram.com/yourhandle" />
              </div>
            </Field>
          </div>
        </Section>

        {/* Banking */}
        <Section title="Banking & Payment Details">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Field label="BSB">
              <input value={general.bsb || ''} onChange={sg('bsb')} className={inputCls} placeholder="000-000" />
            </Field>
            <Field label="Account Number">
              <input value={general.account_number || ''} onChange={sg('account_number')} className={inputCls} />
            </Field>
            <Field label="Account Name">
              <input value={general.account_name || ''} onChange={sg('account_name')} className={inputCls} />
            </Field>
            <Field label="PayID">
              <input value={general.payid || ''} onChange={sg('payid')} className={inputCls} placeholder="Phone or email" />
            </Field>
          </div>
          <div className="bg-blue-50 rounded-xl px-4 py-3 mt-3 text-xs text-apex-blue-700 font-medium">
            These details print on every invoice automatically.
          </div>
        </Section>

        {/* Invoice Settings */}
        <Section title="Invoice Settings">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Payment Terms">
              <input value={general.payment_terms || ''} onChange={sg('payment_terms')} className={inputCls} placeholder="Payment due within 14 days" />
            </Field>
            <div />
            <div className="col-span-2">
              <Field label="Invoice Footer Note">
                <textarea value={general.invoice_notes || ''} onChange={sg('invoice_notes')} className={`${inputCls} resize-none`} rows={2} />
              </Field>
            </div>
          </div>
        </Section>

        {/* Email / SMTP Settings */}
        <Section title="Email Settings — Hostinger" icon={Phone}>
          <div className="bg-blue-50 border border-blue-100 rounded-xl px-4 py-3 mb-4 text-xs text-blue-700 space-y-1">
            <p className="font-bold">📧 Hostinger Email Setup:</p>
            <p>1. Go to hPanel → Emails → Email Accounts → create <strong>info@apextowingsolutions.com.au</strong></p>
            <p>2. Copy the password you set, paste it below</p>
            <p>3. Click Save All Settings — invoice emails will work immediately</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="SMTP Host" hint="Hostinger: smtp.hostinger.com">
              <input value={general.smtp_host || 'smtp.hostinger.com'} onChange={sg('smtp_host')} className={inputCls} placeholder="smtp.hostinger.com" />
            </Field>
            <Field label="SMTP Port" hint="587 for TLS (recommended)">
              <input type="number" value={general.smtp_port || '587'} onChange={sg('smtp_port')} className={inputCls} placeholder="587" />
            </Field>
            <Field label="Email Address" hint="The email you created in hPanel">
              <input value={general.smtp_user || ''} onChange={sg('smtp_user')} className={inputCls} placeholder="info@apextowingsolutions.com.au" />
            </Field>
            <Field label="Email Password" hint="Password you set when creating the email in hPanel">
              <input type="password" value={general.smtp_password || ''} onChange={sg('smtp_password')} className={inputCls} placeholder="Your Hostinger email password" />
            </Field>
            <Field label="From Email (shown to customer)" hint="Same as email address above">
              <input value={general.smtp_from || ''} onChange={sg('smtp_from')} className={inputCls} placeholder="info@apextowingsolutions.com.au" />
            </Field>
            <Field label="From Name" hint="Shown as sender name in customer inbox">
              <input value={general.smtp_from_name || ''} onChange={sg('smtp_from_name')} className={inputCls} placeholder="Apex Towing Solutions" />
            </Field>
          </div>
          <div className="bg-green-50 border border-green-200 rounded-xl px-4 py-3 mt-3 text-xs text-green-700">
            ✅ <strong>Hostinger SMTP is pre-filled.</strong> Just add your email address + password above and save.
          </div>
        </Section>

        {/* Change Password */}
        <Section title="Change Admin Password" icon={Phone}>
          <ChangePasswordSection />
        </Section>
        <Section title="AI Blog Generation" icon={Globe}>
          <div className="bg-purple-50 border border-purple-100 rounded-xl px-4 py-3 mb-4 text-xs text-purple-700 font-medium">
            Add your Anthropic API key to enable 1-click AI blog post generation. Sign up free at <strong>console.anthropic.com</strong> — costs less than $0.001 per post.
          </div>
          <Field label="Anthropic API Key" hint="Starts with 'sk-ant-...' · Stored securely in your database · Never shared">
            <input type="password" value={general.anthropic_api_key || ''} onChange={sg('anthropic_api_key')} className={inputCls} placeholder="sk-ant-api03-..." />
          </Field>
        </Section>
        {/* Pricing / Rate Card */}
        <Section title="Pricing / Rate Card">
          <p className="text-xs text-gray-400 mb-3">These rates are used to calculate towing estimates shown to customers on the website.</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              ['Base Rate ($)',           'base_rate'],
              ['Per KM Metro ($)',        'per_km_metro'],
              ['Per KM Rural ($)',        'per_km_rural'],
              ['Emergency Surcharge ($)', 'emergency_surcharge'],
              ['After Hours ($)',         'after_hours_surcharge'],
              ['Heavy Vehicle ($)',       'heavy_vehicle_surcharge'],
              ['Access Fee ($)',          'access_fee'],
            ].map(([l, k]) => (
              <Field key={k} label={l}>
                <input type="number" value={pricing[k] || ''} onChange={sp(k)} className={inputCls} />
              </Field>
            ))}
          </div>
        </Section>

        {/* Cash for Cars Pricing */}
        <Section title="Cash for Cars — Offer Ranges">
          <div className="bg-blue-50 border border-blue-100 rounded-xl px-4 py-3 mb-4 text-xs text-blue-700">
            💡 These values show directly on the customer estimate page — changes take effect immediately after saving.
            The system adjusts for vehicle age and type automatically based on the multipliers below.
          </div>

          {/* Base offers by condition */}
          <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">Base Offer by Condition (Car / Sedan)</p>
          <div className="overflow-x-auto mb-6">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50">
                  <th className="text-left px-4 py-2.5 text-xs font-bold text-gray-500 uppercase rounded-tl-xl">Condition</th>
                  <th className="text-left px-4 py-2.5 text-xs font-bold text-gray-500 uppercase">Min Offer ($)</th>
                  <th className="text-left px-4 py-2.5 text-xs font-bold text-gray-500 uppercase rounded-tr-xl">Max Offer ($)</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {[
                  ['Excellent', 'cash_excellent_min', 'cash_excellent_max', 1500, 3500, 'bg-green-100 text-green-700'],
                  ['Good',      'cash_good_min',      'cash_good_max',      700,  1800, 'bg-blue-100 text-blue-700'],
                  ['Fair',      'cash_fair_min',      'cash_fair_max',      300,  800,  'bg-yellow-100 text-yellow-700'],
                  ['Poor',      'cash_poor_min',      'cash_poor_max',      100,  400,  'bg-orange-100 text-orange-700'],
                  ['Scrap',     'cash_scrap_min',     'cash_scrap_max',     50,   250,  'bg-gray-100 text-gray-600'],
                ].map(([label, kMin, kMax, defMin, defMax, cls]) => (
                  <tr key={label}>
                    <td className="px-4 py-3">
                      <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${cls}`}>{label}</span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5">
                        <span className="text-gray-400 font-bold">$</span>
                        <input type="number" value={pricing[kMin] ?? defMin} onChange={sp(kMin)}
                          className={inputCls} style={{ maxWidth: 120 }} />
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5">
                        <span className="text-gray-400 font-bold">$</span>
                        <input type="number" value={pricing[kMax] ?? defMax} onChange={sp(kMax)}
                          className={inputCls} style={{ maxWidth: 120 }} />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Vehicle type multipliers */}
          <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">Vehicle Type Multipliers</p>
          <p className="text-xs text-gray-400 mb-3">Base offer × multiplier = shown offer. SUV/4WD at 1.15 means 15% more than a car of the same condition.</p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-6">
            {[
              ['Car / Sedan',  'mult_car',   1.0],
              ['SUV / 4WD',   'mult_suv',   1.15],
              ['Ute / Van',   'mult_ute',   1.2],
              ['Truck',       'mult_truck',  1.35],
              ['Motorcycle',  'mult_moto',  0.8],
              ['Heavy Mach.', 'mult_heavy', 2.5],
            ].map(([label, key, def]) => (
              <Field key={key} label={label} hint={`Default: ${def}x`}>
                <div className="flex items-center gap-1.5">
                  <input type="number" step="0.05" value={pricing[key] ?? def} onChange={sp(key)}
                    className={inputCls} />
                  <span className="text-gray-400 font-bold text-sm shrink-0">×</span>
                </div>
              </Field>
            ))}
          </div>

          {/* Age depreciation */}
          <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">Age Depreciation Per Year (%)</p>
          <p className="text-xs text-gray-400 mb-3">How much to reduce the offer for each year of age. Default 3.5% — a 10 year old car gets ~65% of the base offer. Min 40% regardless.</p>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Depreciation per year (%)" hint="Default: 3.5">
              <div className="flex items-center gap-1.5">
                <input type="number" step="0.5" value={pricing.age_depreciation_pct ?? 3.5} onChange={sp('age_depreciation_pct')}
                  className={inputCls} style={{ maxWidth: 120 }} />
                <span className="text-gray-400 font-bold">%</span>
              </div>
            </Field>
            <Field label="Minimum payout floor (%)" hint="Never pay less than this % of base. Default: 40">
              <div className="flex items-center gap-1.5">
                <input type="number" step="5" value={pricing.age_floor_pct ?? 40} onChange={sp('age_floor_pct')}
                  className={inputCls} style={{ maxWidth: 120 }} />
                <span className="text-gray-400 font-bold">%</span>
              </div>
            </Field>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 mt-4 text-xs text-amber-700">
            ⚡ <strong>Live update:</strong> All changes take effect on the customer estimate page immediately after you click Save All Settings. No restart needed.
          </div>
        </Section>

      </div>

      <div className="mt-5 flex justify-end">
        <button onClick={save} className="flex items-center gap-2 bg-apex-blue-700 text-white px-8 py-3 rounded-xl font-bold hover:bg-apex-blue-600 text-base">
          <Save className="w-5 h-5" /> Save All Settings
        </button>
      </div>
    </div>
  );
}